import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TranscriptEditorStore } from '../../TranscriptEditorStore';
import { MiniTimelineStore } from '../FineAlignment/MiniTimelineStore';
import { MiniAudioPlayer } from '../FineAlignment/MiniAudioPlayer';
import { MiniTimelineControls } from '../FineAlignment/MiniTimelineControls';
import { MiniTimelineView } from '../FineAlignment/MiniTimelineView';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { BatchCommand } from '@src/transcript/commands/BatchCommand';
import WaveformData from 'waveform-data';
interface FineAlignmentModalProps {
  position: { x: number; y: number };
  isOpen: boolean;
  onClose: () => void;
  selectedElements: Array<{
    id: string;
    segmentId: string;
    paragraphId: string;
    type: string;
  }>;
  editorStore: TranscriptEditorStore;
  audioUrl?: string;
  waveformData: WaveformData | null;
  onForceAlign?: () => void;
}

/**
 * Modal for fine-tuning element time alignments
 */
const FineAlignmentModal: React.FC<FineAlignmentModalProps> = ({
  position,
  isOpen, 
  onClose,
  selectedElements,
  editorStore,
  audioUrl,
  waveformData,
  onForceAlign
}) => {
  // Reference to the modal container
  const modalRef = useRef<HTMLDivElement>(null);
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timelineStore] = useState(() => new MiniTimelineStore(editorStore));
  const [audioPlayer] = useState(() => new MiniAudioPlayer());
  const [modalHeight, setModalHeight] = useState(550);
  const [isInstructionsVisible, setIsInstructionsVisible] = useState(false);
  
  // Handle playback controls
  const handlePlayPause = useCallback(() => {
    if (timelineStore.isPlaying) {
      audioPlayer.pause();
    } else {
      audioPlayer.play();
    }
  }, [timelineStore.isPlaying, audioPlayer]);
  
  // Close on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Space') {
        handlePlayPause();
      }
    };

    // Prevent selection changes when interacting with the modal
    const handleModalEvents = (e: Event) => {
      if (e.target === modalRef.current?.parentElement) {
        e.stopPropagation(); // Prevent event from bubbling up
      }
    };

    // Get the modal and overlay elements
    const modalElement = modalRef.current;
    const overlayElement = modalElement?.parentElement; // The ModalOverlay is parent of ModalContent

    // // Add event listeners to the modal content
    if (modalElement) {
      modalElement.addEventListener('mousedown', handleModalEvents, true);
      modalElement.addEventListener('mouseup', handleModalEvents, true);
      modalElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add event listeners to the modal overlay
    if (overlayElement) {
      overlayElement.addEventListener('mousedown', handleModalEvents, true);
      overlayElement.addEventListener('mouseup', handleModalEvents, true);
      overlayElement.addEventListener('mousemove', handleModalEvents, true);
    }


    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Clean up modal content listeners
      if (modalElement) {
        modalElement.removeEventListener('mousedown', handleModalEvents, true);
        modalElement.removeEventListener('mouseup', handleModalEvents, true);
        modalElement.removeEventListener('mousemove', handleModalEvents, true);
      }

      // Clean up overlay listeners
      if (overlayElement) {
        overlayElement.removeEventListener('mousedown', handleModalEvents, true);
        overlayElement.removeEventListener('mouseup', handleModalEvents, true);
        overlayElement.removeEventListener('mousemove', handleModalEvents, true);
      }
    };
  }, [isOpen, onClose, handlePlayPause]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close if the click is directly on the overlay, not on its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Add helper methods for zoom controls
  const handleZoomIn = useCallback(() => {
    timelineStore.zoomIn();
  }, [timelineStore]);

  const handleZoomOut = useCallback(() => {
    timelineStore.zoomOut();
  }, [timelineStore]);

  const handleFitToSelection = useCallback(() => {
    timelineStore.fitToSelection();
  }, [timelineStore]);

  const handleFitToTimeline = useCallback(() => {
    timelineStore.fitToTimeline();
  }, [timelineStore]);
  
  // Initialize the timeline with selected elements and their context
  useEffect(() => {
    if (!isOpen || !selectedElements.length) return;
    
    const initializeTimeline = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        
        // Load selected elements and context (±5 seconds)
        const selectedElementIds = selectedElements
          .filter(el => el.type === 'text')
          .map(el => ({
            elementId: el.id,
            segmentId: el.segmentId,
            paragraphId: el.paragraphId,
          }));
        
        await timelineStore.loadElements(selectedElementIds);
        
        // Load audio if URL provided
        if (audioUrl) {
          try {
            await audioPlayer.loadAudio(
              audioUrl,
              timelineStore.visibleTimeRange[0],
              timelineStore.visibleTimeRange[1]
            );
          } catch (audioErr) {
            console.error('FineAlignmentModal: Error loading audio:', audioErr);
            setError(`Error loading audio: ${audioErr instanceof Error ? audioErr.message : String(audioErr)}`);
          }
        }
        
        // Load waveform data
        if (waveformData) {
          timelineStore.setWaveformData(waveformData);
        }
        
        // Set initial zoom to fit the selected range
        timelineStore.fitToSelection();
        
        setIsLoading(false);
      } catch (err) {
        console.error('FineAlignmentModal: Error initializing timeline:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize timeline');
        setIsLoading(false);
      }
    };
    
    initializeTimeline();
    
    // Cleanup
    return () => {
      audioPlayer.cleanup();
      timelineStore.reset();
    };
  }, [isOpen, selectedElements, timelineStore, audioPlayer, audioUrl, waveformData]);
  
  // Sync audio player with timeline store
  useEffect(() => {
    if (!isOpen) return;
    
    const handleTimeUpdate = (time: number) => {
      timelineStore.setCurrentTime(time);
    };
    
    const handlePlaybackStateChange = (isPlaying: boolean) => {
      timelineStore.setIsPlaying(isPlaying);
    };
    
    // Connect the audio player to the timeline
    audioPlayer.onTimeUpdate = handleTimeUpdate;
    audioPlayer.onPlaybackStateChange = handlePlaybackStateChange;
    
    return () => {
      audioPlayer.onTimeUpdate = null;
      audioPlayer.onPlaybackStateChange = null;
    };
  }, [isOpen, timelineStore, audioPlayer]);
  

  const handleSeek = useCallback((time: number) => {
    audioPlayer.seekTo(time);
  }, [audioPlayer]);
  
  // Toggle instructions visibility
  const toggleInstructions = useCallback(() => {
    setIsInstructionsVisible(prev => !prev);
  }, []);

  // Load saved height preference
  useEffect(() => {
    try {
      const savedHeight = localStorage.getItem('fineAlignmentModalHeight');
      if (savedHeight) {
        setModalHeight(parseInt(savedHeight, 10));
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, []);
  
  // Handle closing the modal
  const handleClose = useCallback(async () => {
    // Just close the modal directly - changes are auto-saved
    // Check if there are any pending changes that haven't been saved yet
    if (timelineStore.pendingChangesCount > 0) {
      setIsLoading(true);
      
      try {
        // Force one final save before closing if needed
        const commands = timelineStore.generateCommands();
        
        if (commands.length > 0) {
          // Create batch command
          const batchCommand = new BatchCommand(
            editorStore.editor,
            commands
          );
          
          // Execute the batch command
          // This will trigger auto-save via the CommandManager's auto-save functionality
          const success = await editorStore.editor.commandManager.execute(batchCommand);
          if (!success) {
            throw new Error('Failed to apply timing adjustments');
          }
          
          // No need to explicitly call saveChanges() as the CommandManager automatically
          // handles saving after command execution
        }
      } catch (err) {
        console.error('Error saving changes before closing:', err);
        // Don't show error to user - just close the modal anyway
      } finally {
        setIsLoading(false);
        onClose();
      }
    } else {
      // No pending changes, just close
      onClose();
    }
  }, [timelineStore, editorStore, onClose]);
  
  // Calculate ideal position for modal
  const modalPosition = {
    top: Math.max(50, Math.min(position.y, window.innerHeight - modalHeight)),
    left: Math.max(50, Math.min(position.x - 400, window.innerWidth - 800))
  };
  
  if (!isOpen) return null;

  return (
    <ModalOverlay
      onClick={handleOverlayClick}
      data-selection-menu="true"
    >
      <ModalContainer
        ref={modalRef}
        style={{...modalPosition, height: modalHeight}}
        onClick={(e) => e.stopPropagation()}
        data-selection-menu="true"
      >
        <ModalHeader>
          <ModalTitle>Fine-tune Alignment</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalContent>
          {isLoading ? (
            <LoadingContainer>
              <SimpleLoadingSpinner text="Loading timeline data..." />
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorText>{error}</ErrorText>
              <RetryButton
                onClick={() => window.location.reload()}
              >
                Retry
              </RetryButton>
            </ErrorContainer>
          ) : (
            <>
              <ControlsContainer>
                <MiniTimelineControls 
                  timelineStore={timelineStore}
                  audioPlayer={audioPlayer}
                  onPlayPause={handlePlayPause}
                  onSeek={handleSeek}
                />
              </ControlsContainer>
              
              <MiniTimelineView 
                timelineStore={timelineStore}
                audioPlayer={audioPlayer}
              />
              
              <InfoTextToggle onClick={toggleInstructions}>
                {isInstructionsVisible ? 'Hide Instructions ▲' : 'Show Instructions ▼'}
              </InfoTextToggle>

              {isInstructionsVisible && (
                <InfoText>
                  <p>Drag element edges to adjust timing. Buffer zones (±5s) are shown in darker color and cannot be edited.</p>
                  <p>Elements are only stacked vertically when their actual time ranges overlap, not just when they appear close visually.</p>
                  <p>Click on waveform or timeline to seek. Use zoom controls or mouse wheel for precision adjustments.</p>
                  <p><strong>Keyboard Shortcuts:</strong> Space (play/pause), Arrow keys (±0.1s, with Shift ±0.01s), +/- (zoom), 0 (fit to timeline), L (toggle loop)</p>
                  <p><strong>Precise Control:</strong> Hold Shift while dragging for 0.01s precision (vs 0.05s normally). Times auto-snap to prevent invalid overlaps.</p>
                </InfoText>
              )}
            </>
          )}
        </ModalContent>
        
        <ModalFooter>
          <SaveStatus>
            {timelineStore.pendingChangesCount > 0
              ? `${timelineStore.pendingChangesCount} unsaved change${timelineStore.pendingChangesCount !== 1 ? 's' : ''}`
              : 'All changes saved'}
          </SaveStatus>
          <FooterButtons>
            {onForceAlign && (
              <AutoAlignButton
                onClick={() => {
                  // Close this modal and trigger force alignment
                  onClose();
                  onForceAlign();
                }}
              >
                Auto Align
              </AutoAlignButton>
            )}
            <CloseButton
              onClick={handleClose}
              disabled={isLoading}
            >
              Close
            </CloseButton>
          </FooterButtons>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  position: absolute;
  background-color: #1a1a2a;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.6);
  width: 800px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #2a2a3c;
  border-bottom: 1px solid #3a3a4c;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #f2d093;
  font-size: 18px;
  font-weight: 600;
`;

const ModalContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #2a2a3c;
  border-top: 1px solid #3a3a4c;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#4a4a5a' : '#6c9ba5'};
  color: ${props => props.disabled ? '#a9a9a9' : '#ffffff'};
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover:not(:disabled) {
    background-color: #7cacb6;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: #f3e9d7;
  border: 1px solid #4a4a6a;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(74, 74, 106, 0.3);
  }
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #f3e9d7;
`;

const LoadingText = styled.p`
  margin-top: 16px;
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #f3e9d7;
  text-align: center;
  padding: 20px;
`;

const ErrorIcon = styled.div`
  font-size: 36px;
  margin-bottom: 16px;
`;

const ErrorText = styled.p`
  font-size: 16px;
  margin-bottom: 20px;
  max-width: 400px;
`;

const RetryButton = styled.button`
  background-color: #a46c85;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #b57d96;
  }
`;

const InfoText = styled.div`
  margin-top: 10px;
  color: #a9a9a9;
  font-size: 13px;
  background-color: rgba(74, 74, 106, 0.2);
  border-radius: 4px;
  padding: 10px;
  animation: fadeIn 0.3s ease;
  max-height: 300px;
  overflow-y: auto;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  p {
    margin: 5px 0;
  }
`;

const InfoTextToggle = styled.button`
  background-color: transparent;
  border: none;
  color: #a9a9a9;
  cursor: pointer;
  font-size: 13px;
  margin-top: 15px;
  text-align: center;
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(74, 74, 106, 0.3);
    color: #f3e9d7;
  }
`;

const SaveStatus = styled.div`
  flex: 1;
  font-size: 14px;
  color: #a9a9a9;
  display: flex;
  align-items: center;
`;

const CloseButton = styled.button<{ disabled?: boolean }>`
  background-color: transparent;
  color: #f3e9d7;
  border: 1px solid #4a4a6a;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover:not(:disabled) {
    background-color: rgba(74, 74, 106, 0.3);
  }
`;

// Container for timeline controls
const ControlsContainer = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;

// Auto align button styling
const AutoAlignButton = styled.button`
  background-color: #a46c85;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #b57d96;
  }
`;

export default FineAlignmentModal;